import React, { useState } from 'react'
import { mdiInformationOutline, mdiPencilOutline } from '@mdi/js'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { commonMessages } from 'app/i18n/messages'
import type { HostPasswordReset } from './types'
//import { WhiteBlock } from 'components/Layout'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  hostPasswordChangeThunk,
  hostPasswordResetThunk,
} from 'features/home/components/HomeConfiguration/slice'
import { useHomeRouteParams } from 'features/home/hooks'
import {
  HostPasswordResetButtonsWrapper,
  HostPasswordResetCancelButton,
  HostPasswordResetContainer,
  HostPasswordResetEditButton,
  HostPasswordResetForm,
  HostPasswordResetFormContent,
  HostPasswordResetFormControl,
  HostPasswordResetHeader,
  HostPasswordResetStyledIcon,
  HostPasswordResetSubmitButton,
  HostPasswordResetTitle,
  HostPasswordResetFormPasswordRow,
  HostPasswordResetFormPassword,
  HostPasswordResetFormPasswordTip,
} from './styles'
import { StyledConfigHeader } from '../styles'
import { TextField, PasswordField } from 'components/Fields'
import { SpinnerCentered } from 'components/Loader'
import { useIntl } from 'react-intl'
import { selectedHomeSelector } from 'features/home/selectors'
import { homeSoftwareVersionSelector } from 'features/home/selectors'
import Tooltip from 'components/Tooltip'

export interface HostPasswordResetFormValues {
  oldPassword: string
  newPassword: string
}

function HomePasswordChange() {
  const { homeId } = useHomeRouteParams()
  const dispatch = useAppDispatch()
  const [editMode, setEditMode] = useState<boolean>(false)
  const [resetSent, setResetSent] = useState<boolean>(false)
  const [passwordUpdated, setPasswordUpdated] = useState<boolean>(false)
  const [passwordUpdatedMsg, setPasswordUpdatedMsg] = useState<string>(
    'Host Password Update Sent'
  )
  const [resetdUpdatedMsg, setResetUpdatedMsg] =
    useState<string>('Reset Request Sent')

  const { formatMessage } = useIntl()
  const homeSoftwareVersion = useAppSelector(homeSoftwareVersionSelector)
  const homeSoftwareVersionMajor =
    homeSoftwareVersion.softwareVersionLabel?.substring(
      0,
      homeSoftwareVersion.softwareVersionLabel?.indexOf('.')
    )

  const currentHome = useAppSelector(selectedHomeSelector.data)
  const accessEnabled = currentHome?.configAccessExpires
    ? currentHome.configAccessExpires > Date.now()
    : false

  const sendReset = async () => {
    try {
      await dispatch(
        hostPasswordResetThunk({
          homeId: homeId,
          payload: {
            oldPassword: '',
            newPassword: '',
          },
        })
      )
    } catch (err: any) {
      setResetUpdatedMsg('Request Reset Failed: ' + err)
      console.log('Error sending reset', err)
    } finally {
      setResetSent(true)
    }
  }

  const handleSubmit = async (
    values: HostPasswordReset,
    { setSubmitting, setStatus }: FormikHelpers<HostPasswordReset>
  ) => {
    try {
      setSubmitting(true)
      await dispatch(
        hostPasswordChangeThunk({
          homeId: homeId,
          payload: {
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
          },
        })
      )
    } catch (err: any) {
      setStatus({ error: true })
      setPasswordUpdatedMsg('Host Password Update Failed: ' + err)
      console.log('Error changing host password', err)
    } finally {
      values.newPassword = ''
      values.oldPassword = ''
      setSubmitting(false)
      setEditMode(false)
      setPasswordUpdated(true)
    }
  }

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .trim()
      .min(12, 'New Password must be at least 12 characters')
      .max(32, 'New Password must be less than 32 characters')
      .required('New Password is required')
      .test(
        'isValidPass',
        'New Password must be at least 12 characters (Minimum one uppercase, one lowercase and one special character)',
        (value: any, context: any) => {
          const hasUpperCase = /[A-Z]/.test(value)
          const hasNumber = /[0-9]/.test(value)
          const hasLowerCase = /[a-z]/.test(value)
          const hasSymbole = /["!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~"]/.test(value)
          let validConditions = 0
          const numberOfMustBeValidConditions = 3
          const conditions = [hasUpperCase, hasLowerCase, hasNumber, hasSymbole]
          conditions.forEach(condition => (condition ? validConditions++ : null))
          if (validConditions >= numberOfMustBeValidConditions) {
            return true
          }
          return false
        },
      ),
    oldPassword: Yup.string()
      .trim()
      .min(3, 'Too short')
      .max(64, 'Too long')
      .required('Old Password is required'),
  })

  /*
  function password_validate(p: string) {
    return /[A-Z]/.test(p) && /[0-9]/.test(p) && !/[aeiou]/.test(p) && /^[@#][A-Za-z0-9]{11,50}$/.test(p)
  }
  */
  return (
    <>
      <StyledConfigHeader>
        <h2>Reset Host Password</h2>
      </StyledConfigHeader>
      <HostPasswordResetContainer>
        <HostPasswordResetContainer>
          Click this button to send home administrator an email to begin host
          password reset. Host system access required.
        </HostPasswordResetContainer>
        <HostPasswordResetButtonsWrapper>
          <HostPasswordResetSubmitButton
            variant="primary"
            disabled={
              !accessEnabled ||
              Number(homeSoftwareVersionMajor) < 11 ||
              resetSent
            }
            onClick={() => sendReset()}
          >
            Send Reset Email
          </HostPasswordResetSubmitButton>

          {resetSent && (
            <HostPasswordResetButtonsWrapper>
              {resetdUpdatedMsg}
            </HostPasswordResetButtonsWrapper>
          )}
        </HostPasswordResetButtonsWrapper>
      </HostPasswordResetContainer>

      <StyledConfigHeader>
        <h2>Change Host Password</h2>
      </StyledConfigHeader>

      <HostPasswordResetContainer>
        <Formik
          initialValues={{ oldPassword: '', newPassword: '' }}
          validationSchema={validationSchema}
          initialStatus={{ error: false }}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formikProps) => (
            <>
              <HostPasswordResetHeader>
                <HostPasswordResetTitle></HostPasswordResetTitle>

                {!editMode && (
                  <HostPasswordResetEditButton
                    onClick={() => {
                      formikProps.setStatus({ error: false })
                      setEditMode(true)
                    }}
                    disabled={!accessEnabled}
                    title="SavantOS 11+ and Home Access Required"
                  >
                    <HostPasswordResetStyledIcon path={mdiPencilOutline} />
                  </HostPasswordResetEditButton>
                )}
              </HostPasswordResetHeader>
              <HostPasswordResetForm>
                <HostPasswordResetFormContent>
                  <TextField
                    name="oldPassword"
                    placeholder="Old Password"
                    disabled={!editMode}
                    autoComplete="off"
                    showLabel
                  />
                  <HostPasswordResetFormPasswordRow>
                    <HostPasswordResetFormPasswordTip>
                      <Tooltip
                        showOn="hover"
                        positions={['left']}
                        fullWidth
                        content={
                          <>
                            New passwords must contain
                            <ul>
                              <li>At least 12 characters</li>
                              <li>At least one capital and lowercase letter</li>
                              <li>At least one number</li>
                              <li>At least one special character</li>
                            </ul>
                          </>
                        }
                      >
                        <HostPasswordResetStyledIcon
                          path={mdiInformationOutline}
                        />
                      </Tooltip>
                    </HostPasswordResetFormPasswordTip>
                    <HostPasswordResetFormPassword>
                      <PasswordField
                        name="newPassword"
                        placeholder="New Password"
                        disabled={!editMode}
                        autoComplete="off"
                        showLabel
                      />
                    </HostPasswordResetFormPassword>
                  </HostPasswordResetFormPasswordRow>
                </HostPasswordResetFormContent>

                {formikProps.status.error && (
                  <HostPasswordResetTitle>
                    {formatMessage(commonMessages.commonError)}
                  </HostPasswordResetTitle>
                )}

                {passwordUpdated && (
                  <HostPasswordResetButtonsWrapper>
                    {passwordUpdatedMsg}
                  </HostPasswordResetButtonsWrapper>
                )}

                {editMode && (
                  <HostPasswordResetFormControl>
                    {formikProps.isSubmitting && (
                      <HostPasswordResetButtonsWrapper>
                        <SpinnerCentered size="40" />
                      </HostPasswordResetButtonsWrapper>
                    )}
                    {!formikProps.isSubmitting && (
                      <HostPasswordResetButtonsWrapper>
                        <HostPasswordResetCancelButton
                          variant="secondary"
                          type="button"
                          onClick={() => {
                            formikProps.resetForm()
                            setEditMode(false)
                          }}
                        >
                          {formatMessage(commonMessages.cancel)}
                        </HostPasswordResetCancelButton>
                        <HostPasswordResetSubmitButton
                          variant="primary"
                          type="submit"
                        >
                          {formatMessage(commonMessages.save)}
                        </HostPasswordResetSubmitButton>
                      </HostPasswordResetButtonsWrapper>
                    )}
                  </HostPasswordResetFormControl>
                )}
              </HostPasswordResetForm>
            </>
          )}
        </Formik>
      </HostPasswordResetContainer>
    </>
  )
}

export default HomePasswordChange
